/**
 * What the room is doing, and what there is to press.
 *
 * A ROOM THAT LOOKS DONE IS A ROOM NOBODY DIGS. Watched from outside: the
 * interview finished, the screen went quiet, and the only way on was to hunt
 * for a button — which, once pressed, appeared to do nothing for ten seconds,
 * because that is how long the dispatcher takes to wake the first seat.
 *
 * So two jobs, and they are the same job: say what one obvious thing to do next
 * is, and while the room is working, show that it IS working. Pure — no React,
 * no fetching — so the rules can be tested and the component stays a rendering
 * of them.
 */

export type RoomPhase =
  /** No question yet: the chat is the whole screen. */
  | 'opening'
  /** Has a question, has never dug. */
  | 'ready'
  /** A dig is running now. */
  | 'digging'
  /** Has dug before, nothing running. */
  | 'rested';

export interface RoomShape {
  question: string;
  running: boolean;
  runsDone: number;
  seats: number;
  /** A dig written and waiting to be started. A report's Dig button, a dig
   *  brief and the chat all STAGE one rather than spending on the spot. */
  staged?: { what: string } | null;
  /** The table being READ right now, and how many cells it is short of.
   *
   *  A person looking at the price map and pressing "Dig deeper" means the
   *  price map. It used to mean the room, so the one screen they were watching
   *  was the one place nothing landed — the table has its own Dig button
   *  further down, but a button you have to go and find is not the next step. */
  table?: { key: string; label: string; holes: number } | null;
  /** Results that came back and no seat has read yet, and about what
   *  processing them costs in billed tokens (`console.process.waiting`). */
  toProcess?: { results: number; tokens: number } | null;
  /** Which dig the room needs next, and why (`boardroom/next_dig.py`). */
  nextDig?: { depth: 'WIDER' | 'DEEPER'; reason: string } | null;
}

export function phaseOf(room: RoomShape): RoomPhase {
  if (!room.question.trim()) return 'opening';
  if (room.running) return 'digging';
  return room.runsDone > 0 ? 'rested' : 'ready';
}

export interface Action {
  key: 'start' | 'research' | 'stop' | 'staff' | 'process';
  label: string;
  /** Said under the button, in the plain terms of what it will cost and do. */
  hint: string;
  intent?: 'WIDER' | 'DEEPER';
  /** The report key this action fills, when it is aimed at one table. */
  table?: string;
}

/**
 * ONE BUTTON, and the questions asked in the window it opens.
 *
 * It used to be two — "Dig wider" and "Dig deeper" — which answered a question
 * nobody had asked: both meant the whole room, so a person reading the price
 * map watched the one table they cared about stay empty. Widening and
 * deepening is WHAT to do; this table or the room is WHERE, and they do not
 * pair. Four buttons would be the honest version of two, and four buttons is a
 * screen with no next step — so the button says the verb and the window asks
 * the rest, at the one moment a person is already about to spend.
 */
function researchAction(room: RoomShape): Action {
  const t = room.table;
  const next = room.nextDig;
  return {
    key: 'research',
    label: 'Research',
    hint: next && !t
      ? `Recommended: ${next.depth === 'DEEPER' ? 'Fill the gaps' : 'Find more'} - ${next.reason}.`
      : t
      ? `Find more, or fill the gaps in ${t.label.toLowerCase()} - you choose which, and where.`
      : 'Find more, or fill the gaps in what is here - you choose which, and where.',
  };
}

/** "690k", "1.2M": a token count as the hint says it. */
export function aboutTokens(n: number): string {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  return `${Math.max(1, Math.round(n / 1000))}k`;
}

/** The ONE thing to press, and the one thing beside it. Never three: a screen
 *  with three equal buttons is a screen with no next step. */
export function actionsFor(room: RoomShape): { primary: Action; secondary?: Action } {
  if (!room.seats) {
    return {
      primary: {
        key: 'staff',
        label: 'Put someone in the room',
        hint: 'Nobody is staffed yet, so there is nobody to send looking.',
      },
    };
  }
  // A DIG THAT IS WRITTEN AND NOT RUNNING IS THE NEXT STEP, whatever else the
  // room could do. Watched live: a staged dig appeared in a strip further down
  // the page, the button that made it looked like it had done nothing, and the
  // dig sat there unstarted. If something is waiting, it IS the button.
  if (room.staged && !room.running) {
    return {
      primary: {
        key: 'start',
        label: 'Start this dig',
        intent: 'DEEPER',
        hint: room.staged.what,
      },
      secondary: {
        key: 'research',
        label: 'Ask for something else',
        hint: 'Drop what is lined up and choose what to research instead.',
      },
    };
  }
  // RESULTS NOBODY HAS PROCESSED COME BEFORE NEW RESEARCH. Searches land after
  // the dig that asked for them has ended - console searches whenever the
  // owner gets to them - and room 54 sat on 255 with nothing on screen to say
  // so. Processing them is a short dig with no new searches, sized to them.
  const pile = room.toProcess;
  if (pile && pile.results > 0 && !room.running) {
    const n = pile.results;
    return {
      primary: {
        key: 'process',
        label: `Process ${n} result${n === 1 ? '' : 's'}`,
        hint: `${n} search result${n === 1 ? '' : 's'} came back and no seat has processed ${
          n === 1 ? 'it' : 'them'
        }. The seats go through each one and add what they find to the board, with no new searches. About ${aboutTokens(
          pile.tokens,
        )} tokens.`,
      },
      secondary: researchAction(room),
    };
  }
  switch (phaseOf(room)) {
    case 'digging':
      return {
        primary: {
          key: 'stop',
          label: 'Stop the dig',
          hint: 'Everything found so far is kept.',
        },
      };
    case 'rested':
      return { primary: researchAction(room) };
    default:
      return {
        primary: {
          key: 'research',
          label: 'Start researching',
          hint: 'The room searches, reads pages and writes down what it finds.',
        },
      };
  }
}

export interface Activity {
  /** Seats mid-turn right now. */
  working: string[];
  /** Searches and page reads out at the browser. */
  fetching: number;
  /** Results back and not yet read by the seat that asked. */
  landed: number;
  turns: number;
  /** Items this dig has put on the board. */
  found: number;
  /** When the dig started, already in words ("12:45 AM"). */
  startedAt?: string | null;
  /** What it found, by kind, already in words ("5 feature · 4 pricing"). */
  foundKinds?: string | null;
}

/**
 * What to say while it works, in the order a person asks it.
 *
 * "Waking the room…" is the important one: a dig takes a few seconds to reach
 * its first seat, and silence there reads as a button that did nothing.
 */
export function activityLine(a: Activity): string {
  const bits: string[] = [];
  if (a.startedAt) bits.push(`started ${a.startedAt}`);
  if (a.working.length) {
    bits.push(
      a.working.length === 1
        ? `${a.working[0]} is working`
        : `${a.working.length} seats working`,
    );
  } else if (!a.turns) {
    bits.push('waking the room…');
  }
  if (a.fetching) bits.push(`${a.fetching} fetching`);
  if (a.landed) bits.push(`${a.landed} to read`);
  if (a.found) bits.push(a.foundKinds ? `${a.found} found (${a.foundKinds})` : `${a.found} found`);
  if (a.turns) bits.push(`${a.turns} turn${a.turns === 1 ? '' : 's'}`);
  return bits.join(' · ');
}

/** Is anything actually moving? Used to decide whether to animate — a spinner
 *  that spins when nothing is happening is a lie. */
export function isMoving(a: Activity): boolean {
  return Boolean(a.working.length || a.fetching || a.landed);
}
